import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Box,
  TextField,
  DialogTitle,
  DialogActions,
  DialogContent,
  Dialog,
  DialogContentText,
  CircularProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import axios from "axios";
import { validateToken } from "../utils/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Home = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [dataHang, setDataHang] = useState([]);
  const [selectHang, setSelectHang] = useState([dataHang]);
  const [displayData, setDisplayData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [file, setFile] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      const fetchedUser = await validateToken();
      setUser(fetchedUser);
    };

    fetchUser();
  }, []);

  useEffect(() => {
    fetchDataTenHang();
    fetchData();
  }, []);

  useEffect(() => {
    if (dataHang?.length > 0) {
      setSelectHang(dataHang);
    }
  }, [dataHang]);

  useEffect(() => {
    applyFilters();
  }, [data, searchTerm, selectHang]);

  const applyFilters = () => {
    let filteredResults = [...data];

    // Apply company filter
    if (selectHang?.length > 0 && selectHang?.length < dataHang?.length) {
      filteredResults = filteredResults.filter((item) => selectHang.includes(item?.TenHang));
    }

    // Apply search filter
    if (searchTerm) {
      filteredResults = filteredResults.filter(
        (item) =>
          item.DM_VatTu?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.MaVT?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.ChungLoai?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.ControlType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.TuoiThoTB?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.Loai?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setDisplayData(filteredResults);
  };

  const handleFilterHang = (event) => {
    const { value } = event.target;

    // Handle "Select All" case
    if (value.includes("all")) {
      setSelectHang(selectHang?.length === dataHang?.length ? [] : dataHang);
      return;
    }

    setSelectHang(value);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    navigate("/login");
  };

  const handleRowClick = (params) => {
    const id = params.row.ID_VatTu;
    navigate(`/detail-vattu/${id}`);
  };

  const openDeleteDialog = (id) => {
    setItemToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setItemToDelete(null);
    setDeleteDialogOpen(false);
  };

  const fetchData = async (search = "") => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_HOST_API}/vattu/search`, {
        params: { search },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      setData(response.data.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const fetchDataTenHang = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_HOST_API}/vattu/tenhang`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setDataHang(response.data.TenHang);
      console.log(response.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert("Please select a file to upload.");
      return;
    }

    setIsUploadLoading(true);
    const formData = new FormData();
    formData.append("files", file);
    try {
      const response = await axios.post(`${process.env.REACT_APP_HOST_API}/vattu/uploads`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setOpen(false);
      setFile(null);
      fetchData(); // Refresh data after upload
      alert(`Upload file thành công`);
    } catch (error) {
      alert(`Error uploading file:", ${error.message}`);
    } finally {
      setIsUploadLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setIsDeleteLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_HOST_API}/vattu/delete/${id}`, [], {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setData(data.filter((item) => item.ID_VatTu !== id));
      closeDeleteDialog();
      alert(`Xóa thành công`);
    } catch (error) {
      alert(`Error deleting item:", ${error}`);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const getImageBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]); // chỉ lấy phần base64
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn("Không thể tải ảnh:", url, error);
      return null;
    }
  };

  const handleExportExcel = async () => {
    // Chuẩn bị dữ liệu export (không có ảnh)
    const exportData = displayData.map((item) => ({
      ID: item.ID_VatTu,
      "Danh mục vật tư": item.DM_VatTu,
      "Mã vật tư": item.MaVT,
      "Chủng loại": item.ChungLoai,
      "Control Type": item.ControlType,
      "Tuổi thọ trung bình": item.TuoiThoTB,
      "Ghi chú": item.GhiChu,
      Loại: item.Loai,
      Hãng: item.TenHang,
    }));

    // Tạo worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Đặt độ rộng cột
    const columnWidths = [
      { wch: 10 }, // ID
      { wch: 30 }, // Danh mục vật tư
      { wch: 20 }, // Mã vật tư
      { wch: 20 }, // Chủng loại
      { wch: 15 }, // Control Type
      { wch: 20 }, // Tuổi thọ trung bình
      { wch: 30 }, // Ghi chú
      { wch: 20 }, // Loai
      { wch: 20 }, // Hãng
    ];
    ws["!cols"] = columnWidths;

    // Thêm cột ảnh (ẩn hoặc hiển thị)
    const imageColIndex = exportData[0] ? Object.keys(exportData[0]).length : 9;
    ws["!cols"].push({ wch: 20 }); // Cột ảnh

    // Tạo workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Danh sách vật tư");

    // Thêm ảnh vào từng dòng
    if (!wb.Workbook) wb.Workbook = {};
    if (!wb.Workbook.WSImages) wb.Workbook.WSImages = [];

    const imagePromises = displayData.map(async (item, index) => {
      if (item.Anh) {
        const imageUrl = `https://api.pmcweb.vn/upload/${item.Anh}`;
        const base64 = await getImageBase64(imageUrl);
        if (base64) {
          const rowIndex = index + 2; // +1 header, +1 offset
          const cellAddress = XLSX.utils.encode_cell({ c: imageColIndex, r: rowIndex });

          wb.Workbook.WSImages.push({
            name: `image_${item.ID_VatTu}`,
            data: base64,
            opts: {
              "!ref": cellAddress,
              base64: true,
            },
            type: "image",
            extension: "png", // giả sử là png, hoặc detect từ blob
            position: {
              type: "oneCell",
              x: 10,
              y: 10,
              width: 80,
              height: 80,
            },
          });

          // Ghi chú ảnh vào ô (tùy chọn)
          ws[cellAddress] = { t: "s", v: "Ảnh" };
        }
      }
    });

    // Đợi tất cả ảnh tải xong
    await Promise.all(imagePromises);

    // Ghi file với ảnh
    const wbout = XLSX.write(wb, {
      bookType: "xlsx",
      bookSST: false,
      type: "binary",
      cellStyles: true,
      compression: true,
    });

    // Chuyển binary string thành Blob
    const s2ab = (s) => {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    };

    const blob = new Blob([s2ab(wbout)], { type: "application/octet-stream" });
    const timestamp = new Date().toISOString().slice(0, 10);
    const fileName = `DanhSachVatTu_${timestamp}.xlsx`;

    saveAs(blob, fileName);
  };

  const columns = [
    { field: "ID_VatTu", headerName: "ID", width: 80 },
    { field: "DM_VatTu", headerName: "Danh mục vật tư", width: 200 },
    {
      field: "Anh",
      headerName: "Ảnh",
      width: 150,
      renderCell: (params) =>
        params.value ? (
          <img
            src={`https://api.pmcweb.vn/upload/${params.value}`}
            alt="Ảnh"
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "100px",
              objectFit: "cover",
            }}
          />
        ) : (
          <span>Không có ảnh</span>
        ),
    },
    { field: "MaVT", headerName: "Mã vật tư", width: 150 },
    { field: "ChungLoai", headerName: "Chủng loại", width: 150 },
    { field: "ControlType", headerName: "Control Type", width: 100 },
    { field: "TuoiThoTB", headerName: "Tuổi thọ trung bình", width: 150 },
    { field: "GhiChu", headerName: "Ghi chú", width: 250 },
    { field: "Loai", headerName: "Loại", width: 150 },
    { field: "TenHang", headerName: "Hãng", width: 150 },
    ...(user?.isAction == 1
      ? [
          {
            width: 100,
            renderCell: (params) => (
              <Button
                onClick={(event) => {
                  event.stopPropagation();
                  openDeleteDialog(params.row.ID_VatTu);
                }}
                color="error"
              >
                <DeleteIcon />
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h4" gutterBottom>
          PMC Vật tư
        </Typography>

        <Box sx={{ mb: 2 }}>
          {user?.isAction == 1 && (
            <>
              <Button
                variant="contained"
                color="primary"
                style={{ width: "120px", height: "100%" }}
                onClick={() => navigate(`/newVattu`)}
                sx={{ mr: 2 }}
              >
                Thêm mới
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ width: "150px", height: "100%" }}
                onClick={() => setOpen(true)}
                sx={{ mr: 2 }}
                disabled={isUploadLoading}
              >
                {isUploadLoading ? <CircularProgress size={24} /> : "Upload File"}
              </Button>
            </>
          )}

          <Button variant="contained" color="success" onClick={handleExportExcel} sx={{ mr: 2 }} startIcon={<FileDownloadIcon />}>
            Export Excel
          </Button>

          <Button variant="contained" color="error" onClick={handleLogout}>
            Đăng xuất
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
        {/* FormControl */}
        <FormControl sx={{ width: 200, mt: 1 }}>
          <InputLabel>Tên hãng</InputLabel>
          <Select
            multiple
            value={selectHang}
            onChange={handleFilterHang}
            input={<OutlinedInput label="Tên hãng" />}
            sx={{ textTransform: "capitalize" }}
            renderValue={(selected) => {
              if (selected?.length === dataHang?.length) {
                return "Tất cả";
              }
              return selected.join(", ");
            }}
          >
            <MenuItem value="all">
              <Checkbox
                checked={dataHang?.length > 0 && selectHang?.length === dataHang?.length}
                indeterminate={selectHang?.length > 0 && selectHang?.length < dataHang?.length}
              />
              Tất cả
            </MenuItem>
            {dataHang?.map((hang, index) => (
              <MenuItem key={index} value={hang}>
                <Checkbox checked={selectHang.includes(hang)} />
                {hang}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* TextField */}
        <TextField
          label="Tìm kiếm theo Danh mục, Mã vật tư, Chủng loại, Control Type, Tuổi thọ trung bình hoặc Loại"
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1 }} // Đảm bảo TextField chiếm toàn bộ không gian còn lại
        />
      </Box>

      <Box sx={{ height: 700, width: "100%", mt: 1 }}>
        <DataGrid rows={displayData} columns={columns} pagination={false} onRowClick={handleRowClick} getRowId={(row) => row.ID_VatTu} />
      </Box>

      {/* Upload File Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Upload File</DialogTitle>
        <DialogContent>
          <input type="file" onChange={handleFileChange} />
          {file && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              File đang chọn: {file.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUpload} color="primary" disabled={!file || isUploadLoading}>
            {isUploadLoading ? <CircularProgress size={24} /> : "Upload"}
          </Button>
          <Button
            onClick={() => {
              setFile(null);
              setOpen(false);
            }}
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <DialogContentText>Bạn có chắc chắn muốn xóa mục này không?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDelete(itemToDelete)} color="primary" disabled={isDeleteLoading}>
            {isDeleteLoading ? <CircularProgress size={24} /> : "Xóa"}
          </Button>
          <Button onClick={() => closeDeleteDialog()} color="secondary">
            Hủy
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Home;
